import { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import dayjs from 'dayjs';
import { nanoid } from 'nanoid';

import { CreditCardOutlineIcon } from 'assets/svgs';
import { Button, Text } from 'components';
import { getCookie } from 'common/Cookie';
import { ScheduleGridData } from './types';
import ConfirmModal from './ConfirmModal';
import CheckoutModal from 'pages/MyPage/CheckoutModal';
import AccountTransferModal from 'pages/MyPage/AccountTransferModal';
import SuccessModal from 'pages/MyPage/SuccessModal';

interface PlanChipProps {
  plan: string;
}
interface CheckoutRowProps extends PropsWithChildren {
  label: string;
}
interface ContactCheckoutProps {
  plan: string;
  checkedData: ScheduleGridData[];
  onSave: (data: ScheduleGridData[]) => void;
}

type Currency = 'KRW' | 'USD';
type CountryCode = 'KR' | 'OTHER';

/** ===== 가격 반올림/표시 정책 =====
 * KRW: 10원 단위 반올림(Math.round(x/10)*10), 표시 소수 0자리
 * USD: 소수 둘째 자리 반올림(toFixed(2)), 표시 소수 2자리
 */
function roundKRW(x: number): number {
  return Math.round(x / 10) * 10;
}
function roundUSD(x: number): number {
  return parseFloat(x.toFixed(2));
}
const CURRENCY_SYMBOL: Record<Currency, string> = { KRW: '₩', USD: '$' };
function formatMoney(value: number, currency: Currency): string {
  return currency === 'KRW'
    ? value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    : value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const PlanChip = ({ plan }: PlanChipProps) => {
  const planColor =
    {
      Free: '243, 120, 8',
      'Subscription 2': '120, 150, 200',
      'Subscription 3': '35, 240, 105',
    }[plan] || '50, 50, 50';

  return (
    <div
      className="flex h-6 items-center rounded px-2 py-0.5"
      style={{
        backgroundColor: `rgba(${planColor}, 0.1)`,
        border: `1px solid rgba(${planColor}, 1)`,
        color: `rgba(${planColor}, 1)`,
      }}
    >
      <span className="text-small font-medium">{plan}</span>
    </div>
  );
};

const CheckoutRow = ({ children, label }: CheckoutRowProps) => {
  return (
    <li className="flex w-full flex-row items-center justify-between">
      <Text color="tertiary" weight="regular">
        {label}
      </Text>
      {children}
    </li>
  );
};

const ContactCheckout = ({ plan, checkedData, onSave }: ContactCheckoutProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // 서버에서 내려오는 가입국가/통화/무료정책
  const [country, setCountry] = useState<CountryCode | null>(null);
  const [currency, setCurrency] = useState<Currency>('USD');
  const [freePolicy, setFreePolicy] = useState<boolean>(false);

  // 금액 상태(할인 전 금액)
  const [amount, setAmount] = useState<{ currency: Currency; value: number }>({
    currency: 'USD',
    value: 0,
  });
  // 할인액
  const [discount, setDiscount] = useState(0);

  // 결제/모달 상태
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState<ScheduleGridData[]>([]);
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'toss' | 'accountTransfer' | null>(null);
  const [isAccountTransferModalOpen, setIsAccountTransferModalOpen] = useState(false);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [currentOrderId, setCurrentOrderId] = useState(1);
  const [savedCheckedData, setSavedCheckedData] = useState<ScheduleGridData[]>([]);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [paymentResult, setPaymentResult] = useState<any>(null);

  const token = getCookie('accessToken');

  /** 1) 초기: 가입국가/통화/무료정책 조회 */
  useEffect(() => {
    const fetchMemberCurrency = async () => {
      try {
        const res = await axios.get('/api/member/me/currency', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const { countryCode, currency, freePolicy } = res.data as {
          countryCode: CountryCode;
          currency: Currency;
          freePolicy: boolean;
        };
        setCountry(countryCode);
        setCurrency(currency);
        setFreePolicy(!!freePolicy);
        setAmount(prev => ({ currency, value: prev.value }));
      } catch (e) {
        console.error('Error fetching member currency:', e);
        setCountry('OTHER');
        setCurrency('USD');
        setFreePolicy(false);
        setAmount(prev => ({ currency: 'USD', value: prev.value }));
      }
    };
    fetchMemberCurrency();
  }, [token]);

  /** PayPal 성공 메시지 리스너 추가 */
  useEffect(() => {
    const handlePayPalMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      const data = e.data;
      if (!data || typeof data !== 'object') return;

      if (data.type === 'PAYPAL_SUCCESS') {
        // PayPal 결제 성공 시 모달 표시
        setPaymentResult({
          totalAmount: data.amount,
          orderId: data.orderId
        });
        setIsCheckoutModalOpen(false);
        setIsSuccessModalOpen(true);
      }
      
      if (data.type === 'PAYPAL_FAIL') {
        console.error('[PAYPAL_FAIL]', data.reason);
      }
    };

    window.addEventListener('message', handlePayPalMessage);
    return () => window.removeEventListener('message', handlePayPalMessage);
  }, []);

  /** 2) 선택 슬롯 저장 */
  const handleSaveCheckedData = async (data: ScheduleGridData[]) => {
    try {
      const params = data.map(item => ({
        startTime: dayjs(item.StartTime).format('YYYY-MM-DD HH:mm:ss'),
        endTime: dayjs(item.EndTime).format('YYYY-MM-DD HH:mm:ss'),
        maxElevationDegrees: item.MaxElevation,
        availableTime: item.AvailableTime,
        contactProfileId: item.contactProfileId,
        linkId: item.linkId,
      }));
      await axios.post('/api/schedule/reservation', params, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSavedCheckedData(data);
      onSave?.(data);
    } catch (error) {
      console.error('Error saving checked data:', error);
      alert('Failed to save data.');
    }
  };

  /** 3) 금액 계산 (KRW=10원 반올림, USD=소수2자리 반올림) */
  useEffect(() => {
    if (!country || !currency) return;

    const isKRW = currency === 'KRW';
    const pricePerMinute = isKRW ? 3000 : 2.5;

    const rawTotal = checkedData.reduce(
      (acc, cur) => acc + (cur.AvailableTime / 60) * pricePerMinute,
      0
    );
    const value = isKRW ? roundKRW(rawTotal) : roundUSD(rawTotal);

    setAmount({ currency, value });
    // 무료 정책이면 총액=할인 → 합계 0
    setDiscount(freePolicy ? value : 0);
  }, [checkedData, country, currency, freePolicy]);

  /** 4) 지불 금액(= 할인 적용 후 금액) — 모든 모달에 동일하게 전달 */
  const payable = useMemo(
    () => Math.max(0, amount.value - discount),
    [amount.value, discount]
  );

  /** 5) 결제 흐름 */
  const handleSave = () => {
    setModalData(checkedData);
    setSavedCheckedData(checkedData);
    setIsModalOpen(true);
  };
  const handleStartTossPayment = () => {
    setIsModalOpen(false);
    setPaymentMethod('toss');
    setIsCheckoutModalOpen(true);
  };
  const handleStartAccountTransfer = () => {
    setIsModalOpen(false);
    setPaymentMethod('accountTransfer');
    const id = nanoid();
    const newOrderId = `BT_${id}_${String(currentOrderId).padStart(3, '0')}`;
    setOrderId(newOrderId);
    setIsAccountTransferModalOpen(true);
  };

  /** 6) 결제 성공 후 처리 */
  const handlePaymentSuccess = (result: any) => {
    setPaymentResult(result);
    setIsCheckoutModalOpen(false);
    setIsSuccessModalOpen(true);
  };
  const handleSuccessConfirm = () => {
    setIsSuccessModalOpen(false);
    navigate('/manage-contacts');
  };

  const symbol = CURRENCY_SYMBOL[currency];
  const fmt = (v: number) => formatMoney(v, currency);

  return (
    <>
      <div className="w-[400px] rounded-2xl bg-bg-layer2 p-3 shadow-primary">
        <ul className="flex w-full flex-col gap-2 p-3">
          <CheckoutRow label={t('scheduleContact.checkout.plan')}>
            <PlanChip plan={plan} />
          </CheckoutRow>

          <CheckoutRow label={t('scheduleContact.checkout.price')}>
            <Text>{`${symbol}${fmt(amount.value)}`}</Text>
          </CheckoutRow>

          <CheckoutRow label={t('scheduleContact.checkout.discount')}>
            <Text>{`- ${symbol}${fmt(discount)}`}</Text>
          </CheckoutRow>
        </ul>

        <div className="mt-3 flex flex-row items-center justify-between">
          <div className="pl-3">
            <Text weight="regular">{`${checkedData.length} ${t('scheduleContact.checkout.selected')}`}</Text>
          </div>
          <div className="h-4 w-[1px] bg-border-primary" />
          <div className="flex flex-row items-center gap-2">
            <Text color="tertiary" weight="regular">
              {t('scheduleContact.checkout.total')}
            </Text>
            <Text weight="medium" size="medium">
              {`${symbol}${fmt(payable)}`}
            </Text>
          </div>
          <div className="h-4 w-[1px] bg-border-primary" />
          <Button
            icon={<CreditCardOutlineIcon className="[&_path]:stroke-white" />}
            text={t('scheduleContact.checkout.checkout')}
            type="primary"
            onClick={handleSave}
            aria-label="select payment"
          />
        </div>
      </div>

      {isModalOpen && (
        <ConfirmModal
          data={modalData}
          amount={{ currency, value: payable }}
          country={country}
          onClose={() => setIsModalOpen(false)}
          onStartToss={handleStartTossPayment}
          onStartAccountTransfer={handleStartAccountTransfer}
          freePolicy={freePolicy}
        />
      )}

      <CheckoutModal
        isOpen={isCheckoutModalOpen}
        onClose={() => setIsCheckoutModalOpen(false)}
        amount={{ currency, value: payable }}
        country={country}
        onSave={handleSaveCheckedData}
        data={savedCheckedData}
        setData={setSavedCheckedData}
        onPaymentSuccess={handlePaymentSuccess}
      />

      <AccountTransferModal
        isOpen={isAccountTransferModalOpen}
        amount={{ currency, value: payable }}
        country={country}
        orderId={orderId}
        onClose={() => setIsAccountTransferModalOpen(false)}
        onConfirm={handleSaveCheckedData}
        data={savedCheckedData}
      />

      {isSuccessModalOpen && paymentResult && (
        <SuccessModal
          isOpen={isSuccessModalOpen}
          onClose={handleSuccessConfirm}
          amount={paymentResult.totalAmount}
          orderId={paymentResult.orderId}
          currency={currency}
        />
      )}
    </>
  );
};

export default ContactCheckout;